/**
 * Turning what a control reported into what the record stores.
 *
 * `SettingsChange` is semantic — "the user ticked Reels", "the user removed
 * the second rule" — because the renderer is meant to drop into a kit with no
 * backend. Legacy's storage rules live here instead: the filter group's five
 * separate booleans, the post-type bitmask, echo's minute offsets, the day
 * mask, the shortener select that writes two ids.
 *
 * EVERYTHING COMES OUT AS A LIST OF WRITES, each a dot path and a value —
 * exactly what `FormUtilityMixin.requestChange` consumes. So the host stays
 * four lines: translate, then replay each write through the mixin it already
 * uses. A change that stores nothing (a Reconnect button, a Find-related
 * button) returns an empty list and is handled by the host directly.
 */

import {
  type SettingsChange,
  type EchoRow,
  type ScheduleWindow,
  type SettingsField,
  type TextRule,
} from "@/shadcn/components/settings/settings-form"

import {
  type Constants,
  type LegacyRecord,
  flag,
  readPath,
} from "@/lib/settings-sections"
import {
  HASHTAG_STYLE_FIELD,
  STYLE_AI_GENERATED,
  STYLE_AUTO_DETECT,
  STYLE_FIXED_DYNAMIC,
  echoIndex,
  ruleIndex,
} from "@/lib/feed-settings-sections"

/** One `requestChange`: the `data-field` dot path, and the value to store. */
export type SettingsWrite = {
  field: string
  value: unknown
}

/** Legacy stores its flags as 0/1 ints — see `ToggleControl` + `requestChange`. */
function bit(checked: boolean): number {
  return checked ? 1 : 0
}

function write(field: string | undefined, value: unknown): SettingsWrite[] {
  return field ? [{ field, value }] : []
}

/* ────────────────────────────────────────────────────────────────── echo ── */

/** Legacy's defaults, from `EchoComponent`: one echo a day out, two prefixes. */
const ECHO_DEFAULT_MINUTES = 1440
const ECHO_DEFAULT_PREFIXES = ["ICYMI:", "FYI:"]

/** 1 hour to 31 days, the bounds `EchoComponent.updateReplay` enforces. */
const ECHO_MIN_MINUTES = 60
const ECHO_MAX_MINUTES = 44640

function echoOffsets(input: LegacyRecord): number[] {
  const stored = readPath(input, "attrs.repost")

  return Array.isArray(stored)
    ? stored.map((entry) => parseInt(String(entry ?? ""), 10) || 0)
    : []
}

function echoPrefixes(input: LegacyRecord): string[] {
  const stored = readPath(input, "attrs.echoPrefix")

  return Array.isArray(stored) ? [...stored] : [...ECHO_DEFAULT_PREFIXES]
}

function echoWrites(
  change: SettingsChange,
  input: LegacyRecord
): SettingsWrite[] {
  const offsets = echoOffsets(input)
  const prefixes = echoPrefixes(input)

  if (change.type === "toggle") {
    /* On and off both reset the prefixes, which is what legacy does — the
       switch is "echo this source", not "keep my old echo settings". */
    return [
      { field: "input.attrs.repost", value: change.checked ? [ECHO_DEFAULT_MINUTES] : [] },
      { field: "input.attrs.echoPrefix", value: [...ECHO_DEFAULT_PREFIXES] },
    ]
  }

  if (change.type === "row-add") {
    /* Legacy's "Add second echo" copies the first row's delay rather than
       inventing one (`EchoComponent.addSecondReplay`). */
    return write("input.attrs.repost", [
      ...offsets,
      offsets[0] ?? ECHO_DEFAULT_MINUTES,
    ])
  }

  if (change.type === "row-remove") {
    const index = echoIndex(change.rowId)

    return write(
      "input.attrs.repost",
      offsets.filter((_offset, i) => i !== index)
    )
  }

  if (change.type !== "row-set") return []

  const index = echoIndex(change.rowId)
  const patch = change.patch as Partial<EchoRow>

  if (patch.prefix !== undefined) {
    const next = [...prefixes]
    next[index] = patch.prefix

    return write("input.attrs.echoPrefix", next)
  }

  if (patch.days === undefined && patch.hours === undefined) return []

  /* The row shows days and hours; the record stores one minute offset, so the
     half that did not change has to be read back out of it. */
  const current = offsets[index] ?? ECHO_DEFAULT_MINUTES
  const days =
    patch.days !== undefined
      ? parseInt(patch.days, 10) || 0
      : Math.floor(current / 1440)
  const hours =
    patch.hours !== undefined
      ? parseInt(patch.hours, 10) || 0
      : Math.floor((current % 1440) / 60)
  const minutes = days * 1440 + hours * 60

  /* Out of range is refused rather than clamped, exactly as legacy refuses it:
     silently storing 31 days when 45 was asked for is worse than the control
     appearing not to move. */
  if (minutes < ECHO_MIN_MINUTES || minutes > ECHO_MAX_MINUTES) return []

  const next = [...offsets]
  next[index] = minutes

  return write("input.attrs.repost", next)
}

/* ────────────────────────────────────────────────────────────── schedule ── */

/**
 * Whether a stored window is the one the row is talking about.
 *
 * COMPARED AS STRINGS, because the id makes a round trip that changes its
 * type: the builder reads `s_id` through `text()` so the row can carry it and
 * React can key on it, and a saved window's `s_id` is a NUMBER. Strict
 * equality then never matched, so remove and edit both silently did nothing on
 * any window that had ever been saved — while a freshly added one, whose id is
 * the string `sch-<timestamp>`, worked and hid it.
 *
 * Legacy compares strictly and is fine doing so: it matches two values taken
 * from the same array, so their types always agree. This contract hands the id
 * out and takes it back, which is exactly the case where they do not.
 */
function isWindow(row: LegacyRecord, rowId: string): boolean {
  return String(row?.s_id) === String(rowId)
}

function scheduleWrites(
  change: SettingsChange,
  input: LegacyRecord
): SettingsWrite[] {
  const stored: LegacyRecord[] = Array.isArray(input?.sched) ? input.sched : []

  if (change.type === "row-add") {
    /* `s_id` is a client-side marker until the row is saved; `InputEdit.save`
       drops any `sch-` row that never got a day picked. Prepended, as legacy
       does, so a new window appears at the top rather than below a long list. */
    return write("input.sched", [
      {
        s_id: `sch-${Date.now()}`,
        s_days: 0,
        s_startTime: "00:00:00",
        s_endTime: "23:59:59",
        s_tagID: 0,
      },
      ...stored,
    ])
  }

  if (change.type === "row-remove") {
    return write(
      "input.sched",
      stored.filter((row) => !isWindow(row, change.rowId))
    )
  }

  if (change.type !== "row-set") return []

  const patch = change.patch as Partial<ScheduleWindow>

  return write(
    "input.sched",
    stored.map((row) => {
      if (!isWindow(row, change.rowId)) return row

      const next = { ...row }

      if (patch.days !== undefined) next.s_days = patch.days
      if (patch.start !== undefined) next.s_startTime = patch.start

      if (patch.end !== undefined) {
        /*
         * BOTH TIMES THE SAME MEANS "CHECK ONCE AT START" — a window gates
         * the PULL, feeding `Schedule::findNextPullFromSchedules` and so
         * `i_nextPullTs`, not the post. Legacy spells
         * that `s_endTime: "00:00:00"` — not a window ending at midnight.
         * `ScheduleComponent` writes it when the two slider handles meet, and
         * `ScheduleWindow`'s own doc comment reads it back the same way.
         */
        const start = patch.start ?? row.s_startTime
        next.s_endTime = patch.end === start ? "00:00:00" : patch.end
      }

      return next
    })
  )
}

/* ───────────────────────────────────────────────────────────────── rules ── */

/** The new vocabulary back into legacy's field names. */
function applyRulePatch(
  rule: LegacyRecord,
  patch: Partial<TextRule>
): LegacyRecord {
  const next = { ...rule }

  if (patch.action !== undefined) next.action = patch.action
  if (patch.search !== undefined) next.search = patch.search
  if (patch.replace !== undefined) next.replace = patch.replace
  // Legacy's key is the reserved word `case`.
  if (patch.caseSensitive !== undefined) next["case"] = patch.caseSensitive
  if (patch.wholeWord !== undefined) next.find = patch.wholeWord ? "whole" : "any"

  if (patch.searchTitle !== undefined || patch.searchBody !== undefined) {
    const current: string[] = Array.isArray(rule.fields)
      ? rule.fields
      : String(rule.fields ?? "").split(",").filter(Boolean)
    const title = patch.searchTitle ?? current.indexOf("title") !== -1
    const body = patch.searchBody ?? current.indexOf("body") !== -1

    next.fields = [title ? "title" : null, body ? "body" : null].filter(Boolean)
  }

  return next
}

function ruleWrites(
  change: SettingsChange,
  input: LegacyRecord
): SettingsWrite[] {
  const stored: LegacyRecord[] = Array.isArray(input?.rules) ? input.rules : []

  if (change.type === "row-add") {
    /*
     * Legacy refuses a second empty rule (`requestTextRuleChange` case 'add'),
     * which is what stops the Add button producing an unbounded stack of blank
     * rows. Same refusal here.
     */
    const hasBlank = stored.some(
      (rule) =>
        rule.deleted === undefined &&
        String(rule.search ?? "") === "" &&
        String(rule.replace ?? "") === ""
    )

    if (hasBlank) return []

    return write("input.rules", [
      ...stored,
      {
        action: "replace",
        search: "",
        replace: "",
        "case": false,
        fields: ["title", "body"],
        find: "any",
      },
    ])
  }

  if (change.type === "row-remove") {
    /*
     * MARKED, NOT SPLICED. `InputEdit.save` strips `deleted` rows on the way
     * out, and keeping the array's length fixed is what keeps every other
     * row's index — and so its id — pointing at the same rule.
     */
    const index = ruleIndex(change.rowId)

    return write(
      "input.rules",
      stored.map((rule, i) => (i === index ? { ...rule, deleted: true } : rule))
    )
  }

  if (change.type !== "row-set") return []

  const index = ruleIndex(change.rowId)

  return write(
    "input.rules",
    stored.map((rule, i) =>
      i === index ? applyRulePatch(rule, change.patch as Partial<TextRule>) : rule
    )
  )
}

/* ────────────────────────────────────────────────────────────── hashtags ── */

const STATIC_PATH = "attrs.hashTags.hashStatic"
const POOL_PATH = "attrs.hashTags.hashPool"

/** Legacy prefixes a bare word; `#` alone is not a tag. */
export function normaliseTag(value: string): string {
  const trimmed = value.trim()

  return trimmed === "" || trimmed.startsWith("#") ? trimmed : `#${trimmed}`
}

/** The add box takes several at once, space separated, as legacy's does. */
function splitTags(value: string): string[] {
  return value
    .split(/\s+/)
    .map(normaliseTag)
    .filter((tag) => tag !== "")
}

function tagList(input: LegacyRecord, path: string): string[] {
  const stored = readPath(input, path)

  return Array.isArray(stored) ? stored.map(String) : []
}

/**
 * Whether a tag is already spoken for.
 *
 * ACROSS BOTH LISTS, not just the one being added to — `dupeCheck` treats the
 * fixed tags and the pool as one namespace, because a tag in both would be
 * posted twice on the same item.
 */
function isDuplicate(tag: string, input: LegacyRecord): boolean {
  return (
    tagList(input, STATIC_PATH).indexOf(tag) !== -1 ||
    tagList(input, POOL_PATH).indexOf(tag) !== -1
  )
}

/**
 * Why a hashtag add was refused, in legacy's own words, or null to allow it.
 *
 * Legacy checks these inline and stores the message in `localErrors`; here the
 * host holds it and the builder hangs it off the field. The three rules are
 * `addTextTagsToPool` and `updateHashtags` between them: a tag needs a
 * character, the lists have caps, and a tag can only be in one of them.
 */
function hashtagError(
  field: SettingsField,
  values: string[],
  input: LegacyRecord,
  constants: Constants
): string | null {
  const isPool = field.name?.endsWith("hashPool")
  const path = isPool ? POOL_PATH : STATIC_PATH
  const max = isPool ? constants.HASH_POOL_MAX : constants.HASH_STATIC_MAX
  const noun = isPool ? "dynamic" : "fixed"

  if (!values.length || values.every((tag) => tag === "#")) {
    return "A tag must contain a character (other than # naturally)"
  }

  if (tagList(input, path).length + values.length > max) {
    return `The maximum number of ${noun} tags allowed is: ${max}`
  }

  const dupes = values.filter((tag) => isDuplicate(tag, input))

  if (dupes.length === 1) return `${dupes[0]} is a duplicate tag`
  if (dupes.length > 1) return `${dupes.join(", ")} are duplicate tags`

  return null
}

/**
 * Why an edit was refused, or null. Runs BEFORE the write and stops it, the
 * same way `feedUpgradeKey` runs beside it — so the host reads both and never
 * has to unpick a change it already applied.
 */
export function feedFieldError(
  change: SettingsChange,
  input: LegacyRecord,
  constants: Constants
): string | null {
  if (change.type !== "tag-add" || change.field.kind !== "tags") return null
  /* The picked-from-a-list flavour (YouTube playlists) has no caps and cannot
     produce a duplicate — the control hides what is already taken. */
  if (change.field.options?.length) return null

  return hashtagError(change.field, splitTags(change.value), input, constants)
}

/**
 * Adding to or removing from one of the two hashtag lists.
 *
 * Separate from the generic `tagWrites` because these two carry rules nothing
 * else does: a bare word gets its `#`, the add box takes several at once, and
 * putting anything in the pool switches the pool on — `updateTags` does that
 * last one, and without it a pool with tags in it would sit inert.
 *
 * Refusals are `feedFieldError`'s job and have already stopped the write by
 * the time this runs; the duplicate filter here only guards the same tag
 * arriving twice within one add.
 */
function hashtagWrites(
  change: TagChange,
  input: LegacyRecord
): SettingsWrite[] {
  const field = change.field.name

  if (!field) return []

  const isPool = field.endsWith("hashPool")
  const tags = tagList(input, isPool ? POOL_PATH : STATIC_PATH)

  if (change.type === "tag-remove") {
    return write(
      field,
      tags.filter((tag) => tag !== change.value)
    )
  }

  const added = splitTags(change.value).filter(
    (tag) => tags.indexOf(tag) === -1
  )

  if (!added.length) return []

  const writes: SettingsWrite[] = [{ field, value: [...tags, ...added] }]

  if (isPool && !flag(readPath(input, "attrs.hashTags.poolActive"))) {
    writes.push({ field: "input.attrs.hashTags.poolActive", value: 1 })
  }

  return writes
}

/* ──────────────────────────────────────────────────────────────── shared ── */

/** Just the two tag variants, so `value` is known to be there. */
type TagChange = Extract<SettingsChange, { type: "tag-add" | "tag-remove" }>

/** A tag list: hashtags, or the hashtag pool. The values are the stored ones. */
function tagWrites(
  change: TagChange,
  record: LegacyRecord,
  prefix: string
): SettingsWrite[] {
  const field = change.field.name

  if (!field) return []

  const path = field.startsWith(`${prefix}.`) ? field.slice(prefix.length + 1) : field
  const stored = readPath(record, path)
  const tags: string[] = Array.isArray(stored) ? stored.map(String) : []

  if (change.type === "tag-add") {
    // Legacy rejects duplicates with an inline error; refusing silently here
    // keeps the pool correct, and the chip is already on screen either way.
    return tags.indexOf(change.value) === -1
      ? write(field, [...tags, change.value])
      : []
  }

  return write(field, tags.filter((tag) => tag !== change.value))
}

/* ───────────────────────────────────────────────────────── hashtag style ── */

/**
 * The style select has NO STORED FIELD. It is a reading of four flags —
 * `autoHash`, `hashTags.smartHashtags`, `hashTags.staticActive` and
 * `hashTags.poolActive` — and picking a style means setting the combination
 * that reads back as it. `InputHashtagPanel.changeStyle` does this by cloning
 * `attrs`, rewriting the flags and storing the whole object in one change;
 * same here, so the two agree on every flag either of them touches.
 *
 * Writing the style itself would store a key nothing reads and leave the four
 * flags alone, so the select would snap back on the next render.
 */
function hashtagStyleWrites(
  style: string,
  input: LegacyRecord,
  constants: Constants
): SettingsWrite[] {
  const attrs: LegacyRecord = { ...(input?.attrs ?? {}) }
  const hashTags: LegacyRecord = { ...(attrs.hashTags ?? {}) }

  attrs.hashTags = hashTags

  /* Both list fields have to exist before the Fixed/Dynamic or Smart screens
     can add to them; legacy seeds them on the same switch. */
  const seedLists = () => {
    if (!Array.isArray(hashTags.hashPool)) hashTags.hashPool = []
    if (!Array.isArray(hashTags.hashStatic)) hashTags.hashStatic = []
  }

  switch (style) {
    case STYLE_AUTO_DETECT:
      hashTags.staticActive = 0
      hashTags.poolNum = 0
      hashTags.poolActive = 0
      attrs.autoHash = 1
      break

    case STYLE_FIXED_DYNAMIC:
      attrs.autoHash = 0
      attrs.autoHashNum = 0
      attrs.autoHashSpaces = 0
      attrs.autoHashSource = constants.HASH_SRC_PAGE
      hashTags.poolActive = 1
      hashTags.smartHashtags = 0
      hashTags.smartHashtagQty = 0
      seedLists()
      break

    case STYLE_AI_GENERATED:
      attrs.autoHash = 0
      attrs.autoHashNum = 0
      attrs.autoHashSpaces = 0
      attrs.autoHashSource = constants.HASH_SRC_PAGE
      hashTags.smartHashtags = 1
      hashTags.smartHashtagQty = 5
      hashTags.poolActive = 0
      hashTags.staticActive = 0
      seedLists()
      break

    default:
      attrs.autoHash = 0
      attrs.autoHashNum = 0
      attrs.autoHashSpaces = 0
      attrs.autoHashSource = constants.HASH_SRC_PAGE
      hashTags.staticActive = 0
      hashTags.poolNum = 0
      hashTags.poolActive = 0
      hashTags.smartHashtags = 0
      hashTags.smartHashtagQty = 0
      break
  }

  return write("input.attrs", attrs)
}

/* ──────────────────────────────────────────────────────────── plan gates ── */

/**
 * Which upgrade prompt a gated field opens, by field id.
 *
 * A gated control is still PRESSABLE — legacy's are, and pressing one opens
 * the upgrade path rather than doing nothing, which is the only reason to show
 * it at all. The keys are legacy's own; each differs from both the field name
 * and the resource name, so they are transcribed rather than derived.
 */
const UPGRADE_BY_FIELD: Record<string, string> = {
  "pull-freq": "pullFreq",
  "push-enabled": "pushUpdates",
  echo: "replay",
  "auto-hash": "autoHashtags",
  "static-active": "staticHashtags",
  "hash-static": "staticHashtags",
  "hash-pool": "hashtagPool",
  "smart-qty": "smartHashtags",
}

/** Which resource each hashtag style needs, and what it prompts for. */
const STYLE_GATE: Record<string, { resource: string[]; key: string }> = {
  [STYLE_AUTO_DETECT]: { resource: ["autoHash"], key: "autoHashtags" },
  [STYLE_FIXED_DYNAMIC]: {
    resource: ["hashStatic", "hashPool"],
    key: "staticHashtags",
  },
  [STYLE_AI_GENERATED]: { resource: ["smartHashtags"], key: "smartHashtags" },
}

function styleGate(
  style: string,
  resources: LegacyRecord
): { key: string; blocks: boolean } | null {
  const gate = STYLE_GATE[style]

  if (!gate) return null
  if (gate.resource.some((name) => resources?.[name])) return null

  return {
    key: gate.key,
    /*
     * ONLY SMART HASHTAGS REFUSES THE WRITE. Legacy prompts and stores anyway
     * for the other two — the flags go in, the backend declines to act on
     * them — and only guards the Smart branch behind an `else`. Faithful
     * rather than tidied, because "prompt and store" versus "prompt and
     * refuse" is a product decision that already exists.
     */
    blocks: style === STYLE_AI_GENERATED,
  }
}

/**
 * The upgrade prompt this change should open, if any. The host passes it to
 * `showUpgradeModal`; `feedWrites` independently returns nothing for a change
 * that must not be stored, so the two can be called in either order.
 */
export function feedUpgradeKey(
  change: SettingsChange,
  resources: LegacyRecord
): string | null {
  if (change.field.locked) {
    return UPGRADE_BY_FIELD[change.field.id] ?? null
  }

  if (change.type === "set" && change.field.name === HASHTAG_STYLE_FIELD) {
    return styleGate(change.value, resources)?.key ?? null
  }

  return null
}

/* ────────────────────────────────────────────────────────────────── feed ── */

/**
 * What an automation-settings change writes.
 *
 * `input` is the working record — `InputEdit`'s `state.input` — because most
 * of the composite widgets need the current array to produce the next one.
 * `ctx.clean` is the saved record, which YouTube's scope restores from.
 */
export function feedWrites(
  change: SettingsChange,
  input: LegacyRecord,
  ctx: { constants: Constants; resources: LegacyRecord; clean?: LegacyRecord | null }
): SettingsWrite[] {
  const { field } = change

  /* None of these store anything. A Reconnect and a Find-related are side
     effects the host runs; an Upgrade only ever opens the upgrade path, which
     `feedUpgradeKey` names. */
  if (
    change.type === "action" ||
    change.type === "tag-search" ||
    change.type === "tag-search-clear" ||
    change.type === "upgrade"
  ) {
    return []
  }

  /* A gated control stores nothing either; same reason. */
  if (field.locked) return []

  /*
   * THE DEFAULT LOCATION. Stored as a serialized `[lat, lng]`, read back as
   * `$route['i_defaultGeo'][0]` / `[1]` by InputFeed, InputTikTok, InputYoutube
   * and InputTwitter, which stamp it onto each item as `geoLat` / `geoLong` at
   * pull time. Empty array for none — `!empty()` is how all four test it, so
   * the cleared value has to be `[]` rather than null or a missing key.
   */
  if (change.type === "geo-set") {
    return write(field.name, [change.lat, change.lng])
  }

  if (change.type === "geo-clear") {
    return write(field.name, [])
  }

  if (change.type === "set" && field.name === HASHTAG_STYLE_FIELD) {
    return styleGate(change.value, ctx.resources)?.blocks
      ? []
      : hashtagStyleWrites(change.value, input, ctx.constants)
  }

  /*
   * YOUTUBE'S "POST THESE VIDEOS". The choice itself is the host's to hold;
   * only the id is stored, so the draft always matches what a save sends.
   *
   * "Whole channel" clears the id — to "", never by dropping the key, because
   * `Input::setAttrs` merges and an absent key would keep the old playlist.
   * "One playlist" after that puts the saved playlist back, rather than making
   * the reader find it again; with none saved it stores nothing until the
   * playlist row below it is picked.
   */
  if (change.type === "set" && field.id === "scope") {
    const current = readPath(input, "attrs.playlistID")

    if (change.value === "channel") {
      return current ? write(field.name, "") : []
    }

    const saved = readPath(ctx.clean, "attrs.playlistID")

    return !current && saved ? write(field.name, saved) : []
  }

  if (field.kind === "echo") return echoWrites(change, input)
  if (field.kind === "schedule") return scheduleWrites(change, input)
  if (field.kind === "rules") return ruleWrites(change, input)

  /* Both tag fields on this screen are hashtag lists. */
  if (change.type === "tag-add" || change.type === "tag-remove") {
    return hashtagWrites(change, input)
  }

  if (change.type === "set") return write(field.name, change.value)

  if (change.type === "toggle") return write(field.name, bit(change.checked))

  if (change.type === "option") {
    /*
     * THE FILTER GROUP IS NOT ONE FIELD. It reads as a single checkbox group
     * and stores five independent booleans — `filtersTitle`, `filtersContent`
     * and so on — so the option's value IS the field name.
     */
    if (field.name === "input.filters") {
      return write(`input.${change.value}`, bit(change.checked))
    }

    /* The post-type filter is a bitmask in one field instead. */
    if (field.name === "input.attrs.typeFilter") {
      const current = parseInt(String(readPath(input, "attrs.typeFilter") ?? ""), 10) || 0
      const flagBit = parseInt(change.value, 10) || 0

      return write(
        field.name,
        change.checked ? current | flagBit : current & ~flagBit
      )
    }

    return []
  }

  return []
}

/* ──────────────────────────────────────────────────────────── destination ── */

/**
 * What a destination-settings change writes.
 *
 * Simpler than the feed's — a destination has no rules, schedule or echo — but
 * it has the one field that writes two paths.
 */
export function socialWrites(
  change: SettingsChange,
  social: LegacyRecord
): SettingsWrite[] {
  const { field } = change

  if (
    change.type === "action" ||
    change.type === "tag-search" ||
    change.type === "tag-search-clear" ||
    change.type === "upgrade" ||
    /* No destination has a map; only a source carries a location. */
    change.type === "geo-set" ||
    change.type === "geo-clear"
  ) {
    return []
  }

  if (change.type === "tag-add" || change.type === "tag-remove") {
    /*
     * YouTube playlists store `{id, title}` pairs, and the chips carry titles
     * — see `TagsControl`. So the title is matched back to its option to
     * recover the id, and a title with no option behind it is dropped rather
     * than stored half-formed.
     */
    if (field.name === "social.params.playLists") {
      const stored: LegacyRecord[] = Array.isArray(readPath(social, "params.playLists"))
        ? (readPath(social, "params.playLists") as LegacyRecord[])
        : []

      if (change.type === "tag-remove") {
        return write(
          field.name,
          stored.filter((playlist) => String(playlist?.title) !== change.value)
        )
      }

      const option = (field.options ?? [])
        .map((entry) => (typeof entry === "string" ? { value: entry, label: entry } : entry))
        .find((entry) => entry.label === change.value)

      if (!option) return []

      return write(field.name, [...stored, { id: option.value, title: option.label }])
    }

    return tagWrites(change, social, "social")
  }

  if (change.type === "set") {
    /*
     * ONE SELECT, TWO STORED IDS. Its option values are `shortener-<id>` and
     * `domain-<id>`, and picking either clears the other — `HelperPanel`
     * dispatches the same pair of changes. The reset goes first so a
     * re-render between the two never shows both set. The id is parsed, as
     * the route's is, so putting the stored choice back matches it.
     */
    if (field.name === "social.saID") {
      const [kind, id] = String(change.value).split("-")
      const stored = parseInt(id, 10) || 0

      return kind === "domain"
        ? [
            { field: "social.saID", value: 0 },
            { field: "social.pdID", value: stored },
          ]
        : [
            { field: "social.pdID", value: 0 },
            { field: "social.saID", value: stored },
          ]
    }

    return write(field.name, change.value)
  }

  if (change.type === "toggle") return write(field.name, bit(change.checked))

  return []
}

/* ─────────────────────────────────────────────────────────────── route ── */

/**
 * What a route-settings change writes.
 *
 * Smaller than the source's — no rules, schedule, echo, hashtags or map — but
 * it carries the two fields that do not write what they appear to.
 */
export function routeWrites(
  change: SettingsChange,
  route: LegacyRecord
): SettingsWrite[] {
  const { field } = change

  if (
    change.type === "action" ||
    change.type === "tag-search" ||
    change.type === "tag-search-clear" ||
    change.type === "upgrade" ||
    change.type === "geo-set" ||
    change.type === "geo-clear"
  ) {
    return []
  }

  if (change.type === "toggle") {
    /*
     * "USE MY ACCOUNT'S DEFAULT" HAS NO FLAG OF ITS OWN. It is `urlAppend`
     * being NULL, so the toggle writes null to turn on and an empty string to
     * turn off — the empty string being what hands the textarea beside it
     * something to edit. `defaultToggle` does the same, and the two controls
     * share the dot path on purpose.
     */
    if (field.name === "route.urlAppend") {
      return write(field.name, change.checked ? null : "")
    }

    return write(field.name, bit(change.checked))
  }

  if (change.type === "option") {
    /* Five independent booleans, as on the source screen. */
    if (field.name === "route.filters") {
      return write(`route.${change.value}`, bit(change.checked))
    }

    return []
  }

  if (change.type === "set") {
    if (field.name === "route.saID") {
      /*
       * THREE OUTCOMES, one more than a destination has. A route may defer to
       * the account default, which neither id can express, so `sysdef` writes
       * NULL to both — distinct from `shortener-0`, which is an explicit
       * "don't shorten". The ids are parsed because they arrive off a control
       * as strings, and legacy stores them as numbers.
       */
      if (change.value === "sysdef") {
        return [
          { field: "route.saID", value: null },
          { field: "route.pdID", value: null },
        ]
      }

      const [kind, id] = String(change.value).split("-")
      const stored = parseInt(id, 10) || 0

      return kind === "domain"
        ? [
            { field: "route.saID", value: 0 },
            { field: "route.pdID", value: stored },
          ]
        : [
            { field: "route.pdID", value: 0 },
            { field: "route.saID", value: stored },
          ]
    }

    return write(field.name, change.value)
  }

  return []
}

/* ──────────────────────────────────────────────────────────── the override ── */

/**
 * What one automation's override of a destination writes.
 *
 * TWO THINGS ARE ITS OWN. Everything writes under `output.paParams`, which is
 * NULL while the automation inherits — so the first edit has to give the field
 * somewhere to land, and that is a copy of the destination's whole params
 * object, seeded by `overrideWrites` in the same batch as the field's own
 * write. `OutputConfigModal.trimParams` reduces the copy back to the keys that
 * actually differ on the way out, so the snapshot never becomes a permanent
 * fork.
 *
 * `output.activeFlag` is the exception: it belongs to the output row rather
 * than to the params, and it writes whether or not anything is overridden.
 */
export function overrideWrites(
  change: SettingsChange,
  output: LegacyRecord,
  social: LegacyRecord
): SettingsWrite[] {
  const { field } = change

  if (
    change.type === "action" ||
    change.type === "tag-search" ||
    change.type === "tag-search-clear" ||
    change.type === "upgrade" ||
    change.type === "geo-set" ||
    change.type === "geo-clear"
  ) {
    return []
  }

  const writes: SettingsWrite[] = []

  /*
   * SEEDING COMES FIRST, in the same batch. The host replays these in order
   * through `requestChange`, which mutates state in place, so the clone is
   * present by the time the field's own write walks the path into it.
   */
  if (
    field.name?.startsWith("output.paParams.") &&
    (output?.paParams === null || output?.paParams === undefined)
  ) {
    writes.push({
      field: "output.paParams",
      value: { ...(social?.params ?? {}) },
    })
  }

  if (change.type === "toggle") {
    writes.push({ field: field.name!, value: bit(change.checked) })

    return writes
  }

  if (change.type === "set") {
    writes.push({ field: field.name!, value: change.value })

    return writes
  }

  if (change.type === "tag-add" || change.type === "tag-remove") {
    /* Only YouTube playlists reach this on a destination. */
    const stored = readPath(output, "paParams.playLists")
    const list: LegacyRecord[] = Array.isArray(stored)
      ? stored
      : Array.isArray(readPath(social, "params.playLists"))
        ? (readPath(social, "params.playLists") as LegacyRecord[])
        : []

    if (change.type === "tag-remove") {
      writes.push({
        field: field.name!,
        value: list.filter((entry) => String(entry?.title) !== change.value),
      })

      return writes
    }

    const option = (field.options ?? [])
      .map((entry) => (typeof entry === "string" ? { value: entry, label: entry } : entry))
      .find((entry) => entry.label === change.value)

    if (!option) return []

    writes.push({
      field: field.name!,
      value: [...list, { id: option.value, title: option.label }],
    })

    return writes
  }

  return []
}

/**
 * Stop overriding: hand every setting back to the destination.
 *
 * `null` is the whole of it — `OutputRule::getParams()` merges the output's
 * params over the destination's, so an absent override is one that tracks.
 */
export function overrideResetWrites(): SettingsWrite[] {
  return [{ field: "output.paParams", value: null }]
}
